import React, { useState, useEffect } from 'react'
import PaymentForm from '../components/PaymentForm'
import PaymentConfirmation from '../components/PaymentConfirmation'
import LoadingSpinner from '../components/LoadingSpinner'
import { useCompatRouter } from '../hooks/useCompatRouter'
import { getApiUrl } from '../utils/api'
import { createEftBookingReservation } from '../services/paymentSupabase'

interface PaymentData {
  transactionId: string
  amount: number
  method: string
  status: 'success' | 'failed' | 'pending'
  timestamp: string
}

interface BookingData {
  roomId: string
  roomName: string
  roomType: string
  checkIn: string
  checkOut: string
  guests: number
  confirmationNumber: string
  guestName: string
  guestEmail: string
  guestPhone: string
  address: string
  city: string
  zipCode: string
  country: string
  baseRate: number
  adminFee: number
  breakageDeposit: number
  totalAmount: number
  specialRequests: string
  bookingSource?: string
  accessToken?: string
}

const getJohannesburgDateString = (date = new Date()) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Africa/Johannesburg',
}).format(date)

type StoredGuestBooking = BookingData & PaymentData & {
  id: string
  bookedDate: string
  bookingStatus: string
  paymentStatus: string
}

type AvailabilityConflictState = {
  message: string
  bookingReference: string
  followUpRequired: boolean
}

type BookingEmailStatus = {
  sent: boolean
  recipient: string | null
  warning: string | null
}

interface YocoStatusResponse {
  bookingReference: string
  bookingStatus: string
  paymentStatus: string
  totalAmount: number
  updatedAt: string
  payment: {
    id: string
    reference: string
    status: string
    method: string
    paidAt: string | null
    emailDelivery?: {
      guestConfirmation?: {
        sent?: boolean
        recipient?: string | null
        warning?: string | null
      } | null
    } | null
  } | null
}

const getQueryString = (value: string | string[] | undefined, fallback = ''): string => {
  if (Array.isArray(value)) {
    return value[0] || fallback
  }

  return value || fallback
}

const upsertGuestBookingSnapshot = (booking: StoredGuestBooking) => {
  if (typeof window === 'undefined') {
    return
  }

  const existingBookings = JSON.parse(localStorage.getItem('guestBookings') || '[]')
  const normalized = Array.isArray(existingBookings) ? existingBookings : []
  const nextBookings = normalized.filter((entry: { confirmationNumber?: string }) => entry.confirmationNumber !== booking.confirmationNumber)
  nextBookings.push(booking)
  localStorage.setItem('guestBookings', JSON.stringify(nextBookings))
}

const PaymentPage: React.FC = () => {
  const router = useCompatRouter()
  
  const [loading, setLoading] = useState(true)
  const [paymentCompleted, setPaymentCompleted] = useState(false)
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null)
  const [bookingData, setBookingData] = useState<BookingData | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [awaitingYocoConfirmation, setAwaitingYocoConfirmation] = useState(false)
  const [eftSubmissionInProgress, setEftSubmissionInProgress] = useState(false)
  const [availabilityConflict, setAvailabilityConflict] = useState<AvailabilityConflictState | null>(null)
  const [bookingEmailStatus, setBookingEmailStatus] = useState<BookingEmailStatus | null>(null)

  const handleAvailabilityConflict = (message: string, followUpRequired = false, bookingReference = '') => {
    setAvailabilityConflict({
      message,
      followUpRequired,
      bookingReference,
    })
    setAwaitingYocoConfirmation(false)
    setPaymentCompleted(false)
    setEftSubmissionInProgress(false)
    setErrorMessage('')
    setBookingEmailStatus(null)
  }

  useEffect(() => {
    // Get booking details from URL params or sessionStorage
    const roomName = getQueryString(router.query.room, 'Ocean Villa')
    const roomId = getQueryString(router.query.roomId, '')
    const roomType = getQueryString(router.query.roomType, roomName.includes('6 Sleeper') ? '6 Sleeper Unit' : '4 Sleeper Unit')
    const checkIn = getQueryString(router.query.checkin, new Date().toISOString().split('T')[0])
    const checkOut = getQueryString(router.query.checkout, new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
    const guests = parseInt(getQueryString(router.query.guests, '2'), 10)
    const guestName = getQueryString(router.query.name, 'Guest User')
    const guestEmail = getQueryString(router.query.email, 'guest@example.com')
    const guestPhone = getQueryString(router.query.phone, '')
    const address = getQueryString(router.query.address, '')
    const city = getQueryString(router.query.city, '')
    const zipCode = getQueryString(router.query.zipCode, '')
    const country = getQueryString(router.query.country, 'South Africa')
    const baseRate = parseFloat(getQueryString(router.query.baseRate, '0'))
    const adminFee = parseFloat(getQueryString(router.query.adminFee, '0'))
    const breakageDeposit = parseFloat(getQueryString(router.query.breakageDeposit, '0'))
    const totalAmount = parseFloat(getQueryString(router.query.amount, '0'))
    const specialRequests = getQueryString(router.query.specialRequests, '')
    const confirmationNumber = getQueryString(router.query.confirmationNumber, `MG${Date.now()}`)
    const bookingSource = getQueryString(router.query.bookingSource, '')
    const accessToken = getQueryString(router.query.accessToken, '')
    const yocoReturn = getQueryString(router.query.yocoReturn, '')

    // Validate required parameters
    if (!roomName || !checkIn || !checkOut) {
      setErrorMessage('Missing booking information. Please start your booking process again.')
      setLoading(false)
      return
    }

    if (bookingSource === 'reservation_desk' && checkIn <= getJohannesburgDateString()) {
      setErrorMessage('Reservation desk bookings must have an arrival date from tomorrow onward. Please return to the reservation desk and choose a later arrival date.')
      setLoading(false)
      return
    }

    // Set booking data
    setBookingData({
      roomId,
      roomName,
      roomType,
      checkIn,
      checkOut,
      guests: Number.isNaN(guests) ? 2 : guests,
      confirmationNumber,
      guestName,
      guestEmail,
      guestPhone,
      address,
      city,
      zipCode,
      country,
      baseRate: Number.isNaN(baseRate) ? 0 : baseRate,
      adminFee: Number.isNaN(adminFee) ? 0 : adminFee,
      breakageDeposit: Number.isNaN(breakageDeposit) ? 0 : breakageDeposit,
      totalAmount: Number.isNaN(totalAmount) ? 0 : totalAmount,
      specialRequests,
      bookingSource,
      accessToken,
    })

    if (yocoReturn === 'cancel') {
      setAvailabilityConflict(null)
      setErrorMessage('YOCO checkout was cancelled before payment completed.')
      setAwaitingYocoConfirmation(false)
    } else if (yocoReturn === 'failure') {
      setAvailabilityConflict(null)
      setErrorMessage('YOCO payment did not complete successfully. Please try again.')
      setAwaitingYocoConfirmation(false)
    } else if (yocoReturn === 'success') {
      setAvailabilityConflict(null)
      setErrorMessage('')
      setAwaitingYocoConfirmation(true)
    } else {
      setAwaitingYocoConfirmation(false)
    }

    setLoading(false)
  }, [router.query])

  useEffect(() => {
    if (!awaitingYocoConfirmation || !bookingData?.accessToken || paymentCompleted) {
      return
    }

    let cancelled = false
    let attempts = 0

    const pollForYocoConfirmation = async () => {
      attempts += 1
      const accessToken = bookingData.accessToken || ''

      try {
        const response = await fetch(`${getApiUrl()}/api/payments/yoco-status/${encodeURIComponent(accessToken)}`)
        if (response.status === 409) {
          const conflictResult = await response.json().catch(() => null)
          if (!cancelled) {
            handleAvailabilityConflict(
              conflictResult?.error || 'This unit is no longer available for your selected dates.',
              Boolean(conflictResult?.data?.followUpRequired),
              String(conflictResult?.data?.bookingReference || bookingData.confirmationNumber)
            )
          }
          return
        }

        if (response.ok) {
          const result = await response.json()
          const status: YocoStatusResponse | undefined = result?.data
          if (result?.success && status?.paymentStatus === 'paid' && status.payment?.reference) {
            if (!cancelled) {
              const guestEmailDelivery = status.payment.emailDelivery?.guestConfirmation
              setPaymentData({
                transactionId: status.payment.reference,
                amount: bookingData.totalAmount,
                method: 'yoco',
                status: 'success',
                timestamp: status.payment.paidAt || new Date().toISOString(),
              })
              setBookingEmailStatus({
                sent: Boolean(guestEmailDelivery?.sent),
                recipient: guestEmailDelivery?.recipient || bookingData.guestEmail,
                warning: guestEmailDelivery?.warning || null,
              })
              setPaymentCompleted(true)
              setAwaitingYocoConfirmation(false)
              setErrorMessage('')
            }
            return
          }
        }
      } catch (error) {
        console.error('Failed to poll Yoco booking status:', error)
      }

      if (cancelled) {
        return
      }

      if (attempts >= 20) {
        setAwaitingYocoConfirmation(false)
        setErrorMessage('Your payment return was received, but confirmation from YOCO is still pending. Please wait a minute and refresh this page, or contact support with your booking reference.')
        return
      }

      window.setTimeout(pollForYocoConfirmation, 3000)
    }

    pollForYocoConfirmation()

    return () => {
      cancelled = true
    }
  }, [awaitingYocoConfirmation, bookingData, paymentCompleted])

  const handlePaymentSuccess = async (payment: PaymentData) => {
    if (!bookingData) {
      setErrorMessage('Missing booking details. Please start again.')
      return
    }

    try {
      if (payment.method !== 'eft') {
        throw new Error('Unsupported direct payment method.')
      }

      setEftSubmissionInProgress(true)
      setErrorMessage('')
      setPaymentData(payment)

      const result = await createEftBookingReservation({
        bookingReference: bookingData.confirmationNumber,
        roomTypeId: Number(bookingData.roomId),
        checkIn: bookingData.checkIn,
        checkOut: bookingData.checkOut,
        guests: bookingData.guests,
        baseRate: bookingData.baseRate,
        adminFee: bookingData.adminFee,
        breakageDeposit: bookingData.breakageDeposit,
        totalAmount: bookingData.totalAmount,
        guestName: bookingData.guestName,
        guestEmail: bookingData.guestEmail,
        guestPhone: bookingData.guestPhone,
        specialRequests: bookingData.specialRequests,
        bookingSource: bookingData.bookingSource,
      })

      setPaymentData(payment)
      setPaymentCompleted(true)
      setErrorMessage('')
      setEftSubmissionInProgress(false)
      setBookingEmailStatus({
        sent: false,
        recipient: result.guestEmail || null,
        warning: result.emailWarning,
      })

      const bookingRecord: StoredGuestBooking = {
        id: `booking_${Date.now()}`,
        ...bookingData,
        ...payment,
        bookedDate: new Date().toISOString(),
        bookingStatus: result.bookingStatus,
        paymentStatus: result.paymentStatus,
      }

      upsertGuestBookingSnapshot(bookingRecord)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create booking.'
      if (message.toLowerCase().includes('no longer available')) {
        handleAvailabilityConflict(message, false, bookingData.confirmationNumber)
        return
      }

      setEftSubmissionInProgress(false)
      setPaymentData(null)
      setErrorMessage(message)
      setPaymentCompleted(false)
    }
  }

  if (availabilityConflict && bookingData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
        <div className="max-w-2xl w-full mx-4">
          <div style={{
            background: 'rgba(245, 158, 11, 0.12)',
            backdropFilter: 'blur(20px)',
            borderRadius: '18px',
            border: '1px solid rgba(245, 158, 11, 0.35)',
            padding: '2rem'
          }}>
            <div style={{
              width: '72px',
              height: '72px',
              background: 'rgba(245, 158, 11, 0.9)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.25rem auto',
              color: '#fff',
              fontSize: '2rem',
              fontWeight: 700
            }}>
              !
            </div>

            <h2 style={{
              color: 'white',
              fontSize: '2rem',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '1rem',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)'
            }}>
              Unit No Longer Available
            </h2>

            <p style={{
              color: 'rgba(255, 255, 255, 0.88)',
              fontSize: '1rem',
              lineHeight: 1.6,
              textAlign: 'center',
              marginBottom: '1rem',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
            }}>
              {availabilityConflict.message}
            </p>

            <div style={{
              background: 'rgba(15, 23, 42, 0.45)',
              borderRadius: '14px',
              padding: '1rem 1.25rem',
              marginBottom: '1.5rem',
              border: '1px solid rgba(255, 255, 255, 0.12)'
            }}>
              <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Booking Reference</div>
              <div style={{ color: 'white', fontWeight: 700, fontSize: '1.05rem' }}>{availabilityConflict.bookingReference || bookingData.confirmationNumber}</div>
            </div>

            <p style={{
              color: 'rgba(255, 255, 255, 0.84)',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              marginBottom: '1.75rem',
              textAlign: 'center',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.6)'
            }}>
              {availabilityConflict.followUpRequired
                ? 'Your payment reached us, but the last available unit was taken before the booking could be finalized. Our staff has been alerted through the audit log and can review any payment follow-up manually.'
                : 'The selected unit was taken before your booking could be finalized. Please return to the reservation desk and choose different dates or another accommodation option.'}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => router.push('/booking-system')}
                style={{
                  padding: '0.95rem 1.25rem',
                  background: 'rgba(8, 145, 178, 0.88)',
                  color: 'white',
                  border: '1px solid rgba(8, 145, 178, 0.6)',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Return to Reservation Desk
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '0.95rem 1.25rem',
                  background: 'rgba(255, 255, 255, 0.08)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Refresh Status
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handlePaymentError = (error: string) => {
    setErrorMessage(error)
    setPaymentCompleted(false)
  }

  const calculateTotal = () => {
    return bookingData?.totalAmount || 0
  }

  if (loading) {
    return (
      <LoadingSpinner 
        size="lg" 
        text="Preparing your payment..." 
        overlay={true}
      />
    )
  }

  if (errorMessage && !bookingData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
        <div className="max-w-md w-full mx-4">
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: 'rgba(239, 68, 68, 0.8)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem auto'
            }}>
              <span style={{ fontSize: '1.5rem' }}>⚠️</span>
            </div>
            
            <h2 style={{
              color: 'white',
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '1rem',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)'
            }}>
              Payment Error
            </h2>
            
            <p style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.9rem',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
              marginBottom: '2rem'
            }}>
              {errorMessage}
            </p>
            
            <button
              onClick={() => router.push('/booking-system')}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'rgba(8, 145, 178, 0.8)',
                color: 'white',
                border: '1px solid rgba(8, 145, 178, 0.5)',
                borderRadius: '12px',
                fontSize: '0.9rem',
                fontWeight: '500',
                backdropFilter: 'blur(8px)',
                cursor: 'pointer',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)'
              }}
            >
              Start New Booking
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (paymentCompleted && paymentData && bookingData) {
    return <PaymentConfirmation paymentData={paymentData} bookingData={bookingData} bookingEmailStatus={bookingEmailStatus} />
  }

  if (eftSubmissionInProgress && bookingData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
        <div className="max-w-2xl w-full mx-4">
          <div style={{
            background: 'rgba(8, 145, 178, 0.14)',
            backdropFilter: 'blur(20px)',
            borderRadius: '18px',
            border: '1px solid rgba(8, 145, 178, 0.35)',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '1.25rem' }}>
              <LoadingSpinner size="lg" text="Finalizing your EFT booking..." />
            </div>

            <h2 style={{
              color: 'white',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.85rem',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)'
            }}>
              Booking Received
            </h2>

            <p style={{
              color: 'rgba(255, 255, 255, 0.88)',
              fontSize: '1rem',
              lineHeight: 1.6,
              marginBottom: '1.25rem',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
            }}>
              We have received your EFT submission and are creating your booking now. Please keep this page open while we finish the confirmation.
            </p>

            <div style={{
              background: 'rgba(15, 23, 42, 0.45)',
              borderRadius: '14px',
              padding: '1rem 1.25rem',
              marginBottom: '1rem',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              textAlign: 'left'
            }}>
              <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Booking Reference</div>
              <div style={{ color: 'white', fontWeight: 700, fontSize: '1.05rem' }}>{bookingData.confirmationNumber}</div>
            </div>

            <p style={{
              color: 'rgba(255, 255, 255, 0.76)',
              fontSize: '0.92rem',
              margin: 0,
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.6)'
            }}>
              This usually takes a few seconds.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'transparent' }}>
      <div style={{
        position: 'sticky',
        top: '0',
        zIndex: 50,
        background: '#0f172a',
        color: '#ffffff',
        padding: '0.75rem 1rem',
        textAlign: 'center',
        fontWeight: 700,
        letterSpacing: '0.04em'
      }}>
        PAYMENT PAGE LOADED
      </div>
      {/* Header */}
      <div style={{ 
        background: 'transparent', 
        borderBottom: 'none',
        padding: '2rem 0'
      }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '2rem',
            borderRadius: '16px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            textAlign: 'center'
          }}>
            <h1 style={{
              fontSize: '3rem',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '1rem',
              textShadow: '3px 3px 6px rgba(0, 0, 0, 0.8)'
            }}>
              Secure Payment
            </h1>
            <p style={{
              fontSize: '1.25rem',
              color: 'rgba(255, 255, 255, 0.9)',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
              margin: 0
            }}>
              Complete your booking for {bookingData?.roomName}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {errorMessage && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            padding: '1rem',
            marginBottom: '2rem'
          }}>
            <div className="flex items-center">
              <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>⚠️</span>
              <p style={{
                color: '#EF4444',
                fontSize: '0.9rem',
                fontWeight: '500',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                margin: 0
              }}>
                {errorMessage}
              </p>
            </div>
          </div>
        )}

        {/* Payment Progress */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div className="flex items-center justify-center space-x-8">
            <div className="flex items-center">
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: '#10B981',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                marginRight: '0.75rem'
              }}>
                ✓
              </div>
              <span style={{
                color: 'white',
                fontSize: '0.9rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                Booking Details
              </span>
            </div>
            
            <div style={{
              width: '40px',
              height: '2px',
              background: '#10B981'
            }} />
            
            <div className="flex items-center">
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: '#0891b2',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                marginRight: '0.75rem'
              }}>
                2
              </div>
              <span style={{
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: '600',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                Payment
              </span>
            </div>
            
            <div style={{
              width: '40px',
              height: '2px',
              background: 'rgba(255, 255, 255, 0.3)'
            }} />
            
            <div className="flex items-center">
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.3)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                marginRight: '0.75rem'
              }}>
                3
              </div>
              <span style={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.9rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                Confirmation
              </span>
            </div>
          </div>
        </div>

        {/* Payment Form */}
        {bookingData && (
          <PaymentForm
            totalAmount={calculateTotal()}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
            bookingDetails={{
              roomName: bookingData.roomName,
              roomType: bookingData.roomType,
              roomTypeId: bookingData.roomId,
              checkIn: bookingData.checkIn,
              checkOut: bookingData.checkOut,
              guests: bookingData.guests,
              baseRate: bookingData.baseRate,
              adminFee: bookingData.adminFee,
              breakageDeposit: bookingData.breakageDeposit,
              confirmationNumber: bookingData.confirmationNumber,
              guestName: bookingData.guestName,
              guestEmail: bookingData.guestEmail,
              guestPhone: bookingData.guestPhone,
              specialRequests: bookingData.specialRequests,
            }}
          />
        )}

        {awaitingYocoConfirmation && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            padding: '1rem',
            marginTop: '1rem',
            textAlign: 'center'
          }}>
            <p style={{
              color: 'white',
              margin: 0,
              fontSize: '0.95rem',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
            }}>
              Waiting for YOCO to confirm your payment and finalize the booking.
            </p>
          </div>
        )}

        {/* Security Notice */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '1rem',
          marginTop: '2rem',
          textAlign: 'center'
        }}>
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center">
              <span style={{ fontSize: '1rem', marginRight: '0.5rem' }}>🔒</span>
              <span style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.8rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                SSL Encrypted
              </span>
            </div>
            
            <div className="flex items-center">
              <span style={{ fontSize: '1rem', marginRight: '0.5rem' }}>🛡️</span>
              <span style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.8rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                PCI Compliant
              </span>
            </div>
            
            <div className="flex items-center">
              <span style={{ fontSize: '1rem', marginRight: '0.5rem' }}>💳</span>
              <span style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.8rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                Secure Payments
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentPage